<script setup>
import { reactive, computed, useTemplateRef, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import ParamRow from './ParamRow.vue';
import AuthConfig from './AuthConfig.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  tool: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('pilotCustomTools/getUIFlags'),
};

const initialState = {
  title: '',
  description: '',
  endpoint_url: '',
  http_method: 'GET',
  request_template: '',
  response_template: '',
  auth_type: 'none',
  auth_config: {},
  param_schema: [],
};

const state = reactive({ ...initialState });

// 标记是否正在编辑模式初始化，避免触发自动同步
const isInitializing = ref(false);

// 标记是否正在处理参数删除，避免 watch 重复同步
const isRemovingParam = ref(false);

// Populate form when in edit mode
watch(
  () => props.tool,
  newTool => {
    if (props.mode === 'edit' && newTool && newTool.id) {
      isInitializing.value = true;
      state.title = newTool.title || '';
      state.description = newTool.description || '';
      state.endpoint_url = newTool.endpoint_url || '';
      state.http_method = newTool.http_method || 'GET';
      state.request_template = newTool.request_template || '';
      state.response_template = newTool.response_template || '';
      state.auth_type = newTool.auth_type || 'none';
      state.auth_config = newTool.auth_config || {};
      state.param_schema = newTool.param_schema || [];
      // 延迟关闭初始化标记，确保 watch 不会触发
      setTimeout(() => {
        isInitializing.value = false;
      }, 0);
    }
  },
  { immediate: true }
);

// 从文本中提取参数名（支持 URL 和请求体模板）
const extractParamsFromText = text => {
  if (!text) return [];
  // 匹配 {{param_name}} 格式，参数名不能为空
  const regex = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
  const params = [];
  let match;
  while ((match = regex.exec(text)) !== null) {
    const paramName = match[1].trim();
    if (paramName && !params.includes(paramName)) {
      params.push(paramName);
    }
  }
  return params;
};

// 从 URL 和请求体模板中提取所有参数
const extractAllParams = () => {
  const urlParams = extractParamsFromText(state.endpoint_url);
  const bodyParams =
    state.http_method === 'POST'
      ? extractParamsFromText(state.request_template)
      : [];
  // 合并并去重
  return [...new Set([...urlParams, ...bodyParams])];
};

// 同步参数到 param_schema
const syncParams = () => {
  // 编辑模式初始化或删除参数时不触发自动同步
  if (isInitializing.value || isRemovingParam.value) return;

  const allParams = extractAllParams();
  const existingParams = state.param_schema.map(p => p.name);

  // 添加新参数（保留已存在参数的数据）
  allParams.forEach(paramName => {
    if (!existingParams.includes(paramName)) {
      state.param_schema.push({
        name: paramName,
        type: 'string',
        description: '',
        required: true,
      });
    }
  });

  // 移除不存在的参数
  for (let i = state.param_schema.length - 1; i >= 0; i--) {
    if (!allParams.includes(state.param_schema[i].name)) {
      state.param_schema.splice(i, 1);
    }
  }
};

// 监听 URL 变化
watch(() => state.endpoint_url, syncParams);

// 监听请求体模板变化（POST 请求）
watch(() => state.request_template, syncParams);

// 监听请求方式变化（GET <-> POST 切换时需要重新同步）
watch(
  () => state.http_method,
  (newMethod, oldMethod) => {
    // 只有在 GET/POST 切换时才重新同步
    if (newMethod !== oldMethod && oldMethod) {
      syncParams();
    }
  }
);

const DEFAULT_PARAM = {
  name: '',
  type: 'string',
  description: '',
  required: false,
};

const REQUEST_TEMPLATE_PLACEHOLDER = `{
  "user_id": "{{user_id}}",
  "parameter": "{{parameter}}"
}`;

const RESPONSE_TEMPLATE_PLACEHOLDER = `订单：{{order_id}}
状态：{{status}}`;

const validationRules = {
  title: { required },
  endpoint_url: { required },
  http_method: { required },
  auth_type: { required },
};

const httpMethodOptions = computed(() => [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
]);

const authTypeOptions = computed(() => [
  { value: 'none', label: t('PILOT.CUSTOM_TOOLS.FORM.AUTH_TYPES.NONE') },
  { value: 'bearer', label: t('PILOT.CUSTOM_TOOLS.FORM.AUTH_TYPES.BEARER') },
  { value: 'basic', label: t('PILOT.CUSTOM_TOOLS.FORM.AUTH_TYPES.BASIC') },
  {
    value: 'api_key',
    label: t('PILOT.CUSTOM_TOOLS.FORM.AUTH_TYPES.API_KEY'),
  },
]);

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(() =>
  props.mode === 'edit'
    ? formState.uiFlags.value.updatingItem
    : formState.uiFlags.value.creatingItem
);

const getErrorMessage = (field, errorKey) => {
  return v$.value[field].$error
    ? t(`PILOT.CUSTOM_TOOLS.FORM.${errorKey}.ERROR`)
    : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  endpoint_url: getErrorMessage('endpoint_url', 'ENDPOINT_URL'),
}));

const paramsRef = useTemplateRef('paramsRef');

const isParamsValid = () => {
  if (!paramsRef.value || paramsRef.value.length === 0) {
    return true;
  }
  return paramsRef.value.every(param => param.validate());
};

const removeParam = index => {
  const paramName = state.param_schema[index]?.name;
  // 标记正在删除参数，避免 watch 重复同步
  isRemovingParam.value = true;

  if (paramName) {
    // 从 URL 中移除 {{ param_name }}
    const urlRegex = new RegExp(`\\{\\{\\s*${paramName}\\s*\\}\\}`, 'g');
    state.endpoint_url = state.endpoint_url.replace(urlRegex, '');

    // 从请求体模板中移除（POST 请求）
    if (state.http_method === 'POST' && state.request_template) {
      try {
        // 尝试解析 JSON 并删除对应键
        const bodyObj = JSON.parse(state.request_template);
        if (bodyObj && typeof bodyObj === 'object') {
          delete bodyObj[paramName];
          state.request_template = JSON.stringify(bodyObj, null, 2);
        }
      } catch {
        // 如果不是有效 JSON，用正则删除 {{ param_name }}
        const bodyRegex = new RegExp(`\\{\\{\\s*${paramName}\\s*\\}\\}`, 'g');
        state.request_template = state.request_template.replace(bodyRegex, '');
      }
    }
  }
  state.param_schema.splice(index, 1);

  // 延迟关闭标记，确保 watch 不会触发
  setTimeout(() => {
    isRemovingParam.value = false;
  }, 0);
};

const addParam = () => {
  state.param_schema.push({ ...DEFAULT_PARAM });
};

const handleCancel = () => emit('cancel');

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid || !isParamsValid()) {
    return;
  }

  emit('submit', state);
};
</script>

<template>
  <form
    class="flex flex-col px-4 -mx-4 gap-4 max-h-[calc(100vh-200px)] overflow-y-scroll"
    @submit.prevent="handleSubmit"
  >
    <Input
      v-model="state.title"
      :label="t('PILOT.CUSTOM_TOOLS.FORM.TITLE.LABEL')"
      :placeholder="t('PILOT.CUSTOM_TOOLS.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <TextArea
      v-model="state.description"
      :label="t('PILOT.CUSTOM_TOOLS.FORM.DESCRIPTION.LABEL')"
      :placeholder="t('PILOT.CUSTOM_TOOLS.FORM.DESCRIPTION.PLACEHOLDER')"
      :rows="2"
    />

    <div class="flex flex-col gap-2">
      <div class="flex gap-2">
        <div class="flex flex-col gap-1 w-28">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">
            {{ t('PILOT.CUSTOM_TOOLS.FORM.HTTP_METHOD.LABEL') }}
          </label>
          <ComboBox
            v-model="state.http_method"
            :options="httpMethodOptions"
            class="[&>div>button]:bg-n-alpha-black2 [&_li]:font-mono [&_button]:font-mono [&>div>button]:outline-offset-[-1px]"
          />
        </div>
        <Input
          v-model="state.endpoint_url"
          :label="t('PILOT.CUSTOM_TOOLS.FORM.ENDPOINT_URL.LABEL')"
          :placeholder="t('PILOT.CUSTOM_TOOLS.FORM.ENDPOINT_URL.PLACEHOLDER')"
          :message="formErrors.endpoint_url"
          :message-type="formErrors.endpoint_url ? 'error' : 'info'"
          class="flex-1"
        />
      </div>
      <p class="text-xs text-n-slate-11">
        {{ t('PILOT.CUSTOM_TOOLS.FORM.ENDPOINT_URL.HELP_TEXT') }}
      </p>
    </div>

    <div v-if="state.http_method === 'POST'" class="flex flex-col gap-2">
      <TextArea
        v-model="state.request_template"
        :label="t('PILOT.CUSTOM_TOOLS.FORM.REQUEST_TEMPLATE.LABEL')"
        :placeholder="REQUEST_TEMPLATE_PLACEHOLDER"
        :rows="4"
        class="[&_textarea]:font-mono"
      />
    </div>

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('PILOT.CUSTOM_TOOLS.FORM.PARAMETERS.LABEL') }}
      </label>
      <ul v-if="state.param_schema.length > 0" class="grid gap-2 list-none">
        <ParamRow
          v-for="(param, index) in state.param_schema"
          :key="param.name"
          ref="paramsRef"
          v-model:name="param.name"
          v-model:type="param.type"
          v-model:description="param.description"
          v-model:required="param.required"
          :readonly-name="true"
          @remove="removeParam(index)"
        />
      </ul>
      <p class="text-xs text-n-slate-11">
        {{ t('PILOT.CUSTOM_TOOLS.FORM.PARAMETERS.AUTO_SYNC_HELP') }}
      </p>
    </div>

    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('PILOT.CUSTOM_TOOLS.FORM.AUTH_TYPE.LABEL') }}
      </label>
      <ComboBox
        v-model="state.auth_type"
        :options="authTypeOptions"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <AuthConfig
      v-model:auth-config="state.auth_config"
      :auth-type="state.auth_type"
    />

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('PILOT.CUSTOM_TOOLS.FORM.RESPONSE_TEMPLATE.LABEL') }}
      </label>
      <TextArea
        v-model="state.response_template"
        :placeholder="RESPONSE_TEMPLATE_PLACEHOLDER"
        :auto-height="true"
        min-height="6rem"
        max-height="16rem"
        class="[&_textarea]:font-mono [&_textarea]:text-sm"
      />
      <p class="text-xs text-n-slate-11 whitespace-pre-line">
        {{ t('PILOT.CUSTOM_TOOLS.FORM.RESPONSE_TEMPLATE.HELP_TEXT') }}
      </p>
    </div>

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('PILOT.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="t(mode === 'edit' ? 'PILOT.FORM.EDIT' : 'PILOT.FORM.CREATE')"
        class="w-full"
        :is-loading="isLoading"
        :disabled="isLoading"
      />
    </div>
  </form>
</template>
